import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Settings, Wind, Activity, Thermometer, PenTool, ShieldCheck, Wrench } from 'lucide-react';
export function FormulaOne() {
  return <div className="pt-24 bg-white" data-id="element-782">
      {/* Top Summary */}
      <section className="py-16 md:py-24 bg-primary-light" data-id="element-783">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-784">
          <div className="max-w-3xl" data-id="element-785">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="inline-flex items-center space-x-2 bg-white px-3 py-1 rounded-full text-sm font-medium text-primary-dark mb-6" data-id="element-786">
              <Settings className="w-4 h-4 text-accent" data-id="element-787" />
              <span data-id="element-788">Engineering / Performance Environment</span>
            </motion.div>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="text-4xl md:text-5xl font-bold text-primary-dark mb-6" data-id="element-789">
              Precision temperature control for a high-performance working
              environment
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="text-xl text-gray-700" data-id="element-790">
              Designed to support comfort, air quality and tightly controlled
              workspace conditions in a demanding engineering setting.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Quick Facts Row */}
      <section className="py-12 border-b border-gray-200" data-id="element-791">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-792">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6" data-id="element-793">
            {[{
            label: 'Sector',
            value: 'Engineering'
          }, {
            label: 'Service',
            value: 'Design & Installation'
          }, {
            label: 'System',
            value: 'Ducted VRV'
          }, {
            label: 'Site',
            value: 'Performance workspace'
          }, {
            label: 'Maintenance',
            value: 'Ongoing contract'
          }, {
            label: 'Location',
            value: 'UK'
          }].map((fact, index) => <div key={index} className="bg-surface p-4 rounded-2xl" data-id="element-794">
                <p className="text-sm text-gray-500 mb-1" data-id="element-795">{fact.label}</p>
                <p className="font-semibold text-primary-dark" data-id="element-796">{fact.value}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Challenge & Approach */}
      <section className="py-16 md:py-24" data-id="element-797">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-798">
          <div className="grid md:grid-cols-2 gap-16" data-id="element-799">
            <div data-id="element-800">
              <h2 className="text-3xl font-bold text-primary-dark mb-6" data-id="element-801">
                The Challenge
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed" data-id="element-802">
                In a high-performance engineering environment, precision is
                everything. The client required a climate control solution that
                could maintain exact temperature and air quality standards
                across a demanding workspace. The existing systems were
                struggling to provide consistent conditions, impacting both
                staff comfort and the stringent environmental requirements
                necessary for precision engineering tasks.
              </p>
            </div>
            <div data-id="element-803">
              <h2 className="text-3xl font-bold text-primary-dark mb-6" data-id="element-804">
                Our Approach
              </h2>
              <ul className="space-y-4" data-id="element-805">
                {['Comprehensive site survey and thermal assessment', 'Bespoke design recommendations tailored to engineering needs', 'Specification of high-efficiency Ducted VRV systems', 'Phased installation scope to minimize operational disruption', 'Integration with advanced control systems for precise monitoring'].map((item, index) => <li key={index} className="flex items-start" data-id="element-806">
                    <CheckCircle2 className="w-6 h-6 text-accent flex-shrink-0 mt-1 mr-3" data-id="element-807" />
                    <span className="text-gray-700 text-lg" data-id="element-808">{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* The Result */}
      <section className="py-16 md:py-24 bg-surface" data-id="element-809">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-810">
          <h2 className="text-3xl font-bold text-primary-dark mb-12 text-center" data-id="element-811">
            The Result
          </h2>
          <div className="grid md:grid-cols-3 gap-8" data-id="element-812">
            {[{
            icon: Thermometer,
            title: 'Comfort & Consistency',
            desc: 'Stable, precise temperature control across all zones, ensuring optimal working conditions.'
          }, {
            icon: Activity,
            title: 'Operational Reliability',
            desc: 'Robust systems designed for continuous operation in a demanding environment.'
          }, {
            icon: Wind,
            title: 'Efficiency & Impact',
            desc: 'Improved air quality and energy efficiency, supporting both staff focus and sustainability goals.'
          }].map((result, index) => <div key={index} className="bg-white p-8 rounded-3xl shadow-sm" data-id="element-813">
                <div className="w-12 h-12 bg-primary-light rounded-xl flex items-center justify-center mb-6" data-id="element-814">
                  <result.icon className="w-6 h-6 text-accent" data-id="element-815" />
                </div>
                <h3 className="text-xl font-bold text-primary-dark mb-3" data-id="element-816">
                  {result.title}
                </h3>
                <p className="text-gray-600" data-id="element-817">{result.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Client Quote */}
      <section className="py-24 bg-primary-dark text-white" data-id="element-818">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-819">
          <svg className="w-12 h-12 text-accent mx-auto mb-8 opacity-50" fill="currentColor" viewBox="0 0 24 24" data-id="element-820">
            <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" data-id="element-821" />
          </svg>
          <blockquote className="text-2xl md:text-3xl font-medium leading-relaxed mb-8" data-id="element-822">
            "In our industry you feel every one percent. Getting air quality and
            temperature right in our offices has been a game-changer for focus
            and performance."
          </blockquote>
          <cite className="block text-lg text-gray-300 not-italic" data-id="element-823">
            — CEO, Automotive Industry
          </cite>
        </div>
      </section>

      {/* Visual Proof */}
      <section className="py-16 md:py-24" data-id="element-824">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-825">
          <div className="rounded-3xl overflow-hidden aspect-video relative" data-id="element-826">
            <img src="https://cdn.magicpatterns.com/uploads/25TP9PqASwBfhD3FYFT9Py/pasted-image.jpg" alt="High-performance engineering environment" className="w-full h-full object-cover" data-id="element-827" />
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 bg-surface" data-id="element-828">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-829">
          <h2 className="text-2xl font-bold text-primary-dark mb-8" data-id="element-830">
            Related Services
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-831">
            {[{
            title: 'Installation',
            icon: PenTool,
            link: '/services/installation'
          }, {
            title: 'Maintenance',
            icon: Wrench,
            link: '/services/maintenance'
          }, {
            title: 'TM44 Compliance',
            icon: ShieldCheck,
            link: '/services/tm44'
          }, {
            title: 'AC support for FM & property teams',
            icon: Settings,
            link: '/services/maintenance'
          }].map((service, index) => <Link key={index} to={service.link} className="bg-white p-6 rounded-2xl hover:shadow-md transition-shadow group flex flex-col items-center text-center" data-id="element-832">
                <service.icon className="w-8 h-8 text-accent mb-4 group-hover:scale-110 transition-transform" data-id="element-833" />
                <span className="font-semibold text-primary-dark" data-id="element-834">
                  {service.title}
                </span>
              </Link>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-primary-dark text-white text-center" data-id="element-835">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-836">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-837">
            Planning a similar project?
          </h2>
          <p className="text-xl text-gray-300 mb-10" data-id="element-838">
            Our commercial team is ready to discuss your requirements and design
            a bespoke solution for your facility.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-839">
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-full text-white bg-accent hover:bg-accent/90 transition-colors" data-id="element-840">
              Book a site survey
              <ArrowRight className="ml-2 w-5 h-5" data-id="element-841" />
            </Link>
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-full text-white border-2 border-white/20 hover:bg-white/10 transition-colors" data-id="element-842">
              Discuss your project
            </Link>
          </div>
        </div>
      </section>
    </div>;
}